<template>
    <Modal2
        v-if="visible"
        design="v3"
        class-name="bulk-property-modal"
        :show-footer="false"
        :show-title="false"
        @cancel="onCancel"
    >
        <div class="bulk-property-modal-content">
            <div class="modal-section">
                <div class="section-title">
                    <i class="material-icons">edit_note</i>
                    <span>{{ $t("auto.addProfileField") }}</span>
                </div>
                
                <div class="properties-wrapper">
                    <div
                        v-for="(prop, index) in properties"
                        :key="index"
                        class="property-card"
                    >
                        <div class="card-header">
                            <div class="header-left">
                                <div class="property-badge">{{ index + 1 }}</div>
                                <span class="property-label">{{ $t("auto.field") }} {{ index + 1 }}</span>
                            </div>
                            <button
                                v-if="properties.length > 1"
                                class="remove-button"
                                @click="removeProperty(index)"
                                :title="$t('auto.removeField')"
                            >
                                <i class="material-icons">close</i>
                            </button>
                        </div>
                        
                        <div class="card-body">
                            <div class="form-field">
                                <label class="field-label">{{ $t("auto.fieldName") }}</label>
                                <a-select
                                    v-model="prop.propertyId"
                                    size="large"
                                    :placeholder="$t('auto.selectField')"
                                    :filter-option="filterOption"
                                    show-search
                                    class="property-select"
                                >
                                    <a-select-option
                                        v-for="contactProp in getAvailablePropertiesForIndex(index)"
                                        :key="contactProp._id"
                                        :value="contactProp._id"
                                    >
                                        {{ contactProp.name }}
                                    </a-select-option>
                                </a-select>
                            </div>

                            <div class="form-field">
                                <label class="field-label">{{ $t("auto.value") }}</label>
                                
                                <a-input
                                    v-if="['text', 'email', 'phone', 'number', 'url'].includes(getPropertyType(prop.propertyId))"
                                    v-model="prop.value"
                                    size="large"
                                    :type="getInputType(prop.propertyId)"
                                    :placeholder="getPlaceholder(prop.propertyId)"
                                />
                                
                                <a-date-picker
                                    v-else-if="getPropertyType(prop.propertyId) === 'date'"
                                    v-model="prop.value"
                                    size="large"
                                    :placeholder="$t('auto.selectDate')"
                                    format="DD.MM.YYYY"
                                    style="width: 100%;"
                                />
                                
                                <a-select
                                    v-else-if="getPropertyType(prop.propertyId) === 'dropdown'"
                                    v-model="prop.value"
                                    size="large"
                                    :placeholder="$t('auto.selectOption')"
                                    style="width: 100%;"
                                >
                                    <a-select-option
                                        v-for="option in getPropertyOptions(prop.propertyId)"
                                        :key="option"
                                        :value="option"
                                    >
                                        {{ option }}
                                    </a-select-option>
                                </a-select>
                                
                                <prism-editor
                                    v-else-if="getPropertyType(prop.propertyId) === 'json'"
                                    v-model="prop.value"
                                    class="json-editor"
                                    :highlight="highlighter"
                                    :line-numbers="false"
                                />

                                <AgentPicker
                                    v-else-if="getPropertyType(prop.propertyId) === 'agent'"
                                    :single="true"
                                    :value="bulkAgentPickerValue(prop.value)"
                                    @input="onBulkAgentPickerInput(prop, $event)"
                                />
                                
                                <a-input
                                    v-else
                                    v-model="prop.value"
                                    size="large"
                                    :placeholder="$t('auto.enterValue')"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <SupsisButton 
                    ghost 
                    block 
                    class="add-more-button" 
                    @click="addProperty"
                >
                    <i class="material-icons">add_circle_outline</i>
                    {{ $t("auto.addNewField") }}
                </SupsisButton>
            </div>

            <div class="modal-footer">
                <SupsisButton @click="onCancel">{{ $t("auto.cancel") }}</SupsisButton>
                <SupsisButton primary @click="onConfirm">
                    <i class="material-icons">check</i>
                    {{ $t("auto.addField") }}
                </SupsisButton>
            </div>
        </div>
    </Modal2>
</template>

<script>
import Modal2 from "@/components/common/Modal2.vue";
import SupsisButton from "@/views/Pages/Settings/components/SupsisButton.vue";
import { PrismEditor } from "vue-prism-editor";
import { mapState } from "vuex";
import AgentPicker from "@/components/pickers/AgentPicker.vue";

export default {
    name: "BulkSetPropertyModal",
    components: { Modal2, SupsisButton, PrismEditor, AgentPicker },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        selectedCount: {
            type: Number,
            default: 0,
        },
    },
    data: function () {
        return {
            properties: [
                {
                    propertyId: null,
                    value: "",
                },
            ],
        };
    },
    computed: {
        ...mapState({
            contactProperties: (state) => state.contactProperty.data || [],
        }),
        availableProperties: function () {
            return this.contactProperties.filter((prop) => !prop.isDeleted);
        },
        getAvailablePropertiesForIndex: function () {
            return (index) => {
                const selectedPropertyIds = this.properties
                    .map((p, i) => (i !== index && p.propertyId ? p.propertyId : null))
                    .filter((id) => id !== null);
                
                return this.availableProperties.filter(
                    (prop) => !selectedPropertyIds.includes(prop._id)
                );
            };
        },
        validProperties: function () {
            return this.properties.filter((prop) => {
                if (!prop.propertyId) return false;
                
                const propType = this.getPropertyType(prop.propertyId);
                
                // Date için moment object da olabilir
                if (propType === 'date') {
                    return prop.value !== null && prop.value !== undefined && prop.value !== '';
                }

                if (propType === "agent") {
                    return !!prop.value && String(prop.value).trim();
                }
                
                // Diğerleri için string check
                return prop.value && (typeof prop.value === 'string' ? prop.value.trim() : true);
            });
        },
    },
    methods: {
        getPropertyName: function (propertyId) {
            const prop = this.contactProperties.find((p) => p._id === propertyId);
            return prop ? prop.name : propertyId;
        },
        getPropertyType: function (propertyId) {
            const prop = this.contactProperties.find((p) => p._id === propertyId);
            return prop ? prop.type : 'text';
        },
        getPropertyOptions: function (propertyId) {
            const prop = this.contactProperties.find((p) => p._id === propertyId);
            return prop && Array.isArray(prop.options) ? prop.options : [];
        },
        getInputType: function (propertyId) {
            const propType = this.getPropertyType(propertyId);
            const typeMap = {
                'email': 'email',
                'phone': 'tel',
                'number': 'number',
                'url': 'url',
            };
            return typeMap[propType] || 'text';
        },
        getPlaceholder: function (propertyId) {
            const propType = this.getPropertyType(propertyId);
            const placeholderMap = {
                'text': 'Metin girin...',
                'email': 'E-posta adresi girin...',
                'phone': 'Telefon numarası girin...',
                'number': 'Sayı girin...',
                'url': 'URL girin...',
            };
            return placeholderMap[propType] || 'Değer girin...';
        },
        highlighter: function (code) {
            return Prism.highlight(code, Prism.languages.javascript, "javascript");
        },
        filterOption: function (input, option) {
            const prop = this.contactProperties.find((p) => p._id === option.key);
            if (!prop) return false;
            return prop.name.toLowerCase().includes(input.toLowerCase());
        },
        bulkAgentPickerValue: function (val) {
            if (!val) {
                return [];
            }
            return [{ type: "agent", value: val }];
        },
        onBulkAgentPickerInput: function (prop, items) {
            var first = items && items[0];
            prop.value = first && first.type === "agent" ? first.value : "";
        },
        addProperty: function () {
            this.properties.push({
                propertyId: null,
                value: "",
            });
        },
        removeProperty: function (index) {
            this.properties.splice(index, 1);
        },
        onConfirm: function () {
            if (this.validProperties.length === 0) {
                this.$message.warning("Lütfen en az bir alan ekleyiniz");
                return;
            }

            // Property value'ları düzenle (date için string'e çevir)
            const processedProperties = this.validProperties.map((prop) => {
                const propType = this.getPropertyType(prop.propertyId);
                let value = prop.value;
                
                // Date için moment object'i ISO string'e çevir
                if (propType === 'date' && value && typeof value === 'object' && value.toISOString) {
                    value = value.toISOString();
                } else if (propType === 'date' && value && this.$moment) {
                    value = this.$moment(value).toISOString();
                }
                
                return {
                    propertyId: prop.propertyId,
                    value: typeof value === 'string' ? value : String(value),
                };
            });

            this.$emit("confirm", {
                action: "add-contact-property",
                payload: {
                    properties: processedProperties,
                },
            });
            this.reset();
        },
        onCancel: function () {
            this.$emit("cancel");
            this.reset();
        },
        reset: function () {
            this.properties = [
                {
                    propertyId: null,
                    value: "",
                },
            ];
        },
    },
};
</script>

<style lang="scss">
.bulk-property-modal {
    .custom-modal-body {
        padding: 0 !important;
    }

    .bulk-property-modal-content {
        display: flex;
        flex-direction: column;
        min-height: 400px;

        .modal-section {
            flex: 1;
            padding: 28px;
            overflow-y: auto;
            max-height: calc(80vh - 200px);
        }

        .section-title {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 2px solid #e8eaed;

            .material-icons {
                font-size: 28px;
                color: #22272d;
            }

            span {
                font-size: 18px;
                font-weight: 700;
                color: #22272d;
                letter-spacing: 0.3px;
            }
        }

        .properties-wrapper {
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-bottom: 20px;
        }

        .property-card {
            background: #ffffff;
            border: 1.5px solid #e8eaed;
            border-radius: 12px;
            overflow: hidden;
            transition: all 0.3s ease;

            &:hover {
                border-color: #22272d;
                box-shadow: 0 4px 12px rgba(34, 39, 45, 0.08);
            }

            .card-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 16px 20px;
                background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
                border-bottom: 1px solid #e8eaed;

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .property-badge {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 28px;
                    height: 28px;
                    background: linear-gradient(135deg, #22272d 0%, #3d4451 100%);
                    color: white;
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 700;
                }

                .property-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #22272d;
                }

                .remove-button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 32px;
                    height: 32px;
                    background: transparent;
                    border: 1.5px solid #e8eaed;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;

                    .material-icons {
                        font-size: 18px;
                        color: #6c757d;
                        transition: color 0.2s ease;
                    }

                    &:hover {
                        background: #fff5f5;
                        border-color: #dc3545;

                        .material-icons {
                            color: #dc3545;
                        }
                    }
                }
            }

            .card-body {
                padding: 20px;
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 16px;

                .form-field {
                    .field-label {
                        display: block;
                        font-size: 13px;
                        font-weight: 600;
                        color: #495057;
                        margin-bottom: 8px;
                        letter-spacing: 0.3px;
                    }
                    
                    .property-select,
                    .ant-select,
                    .ant-input,
                    .ant-calendar-picker {
                        width: 100%;
                    }
                    
                    .json-editor {
                        background: #f7f7f7;
                        border: 1.5px solid #e8eaed;
                        border-radius: 8px;
                        color: #24292e;
                        font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace;
                        font-size: 13px;
                        padding: 12px;
                        min-height: 120px;
                        max-height: 200px;
                        overflow-y: auto;
                        transition: border-color 0.2s ease;
                        
                        &:focus-within {
                            border-color: #22272d;
                        }
                    }
                }
            }
        }

        .add-more-button {
            border-style: dashed !important;
            border-width: 2px !important;
            height: 48px !important;
            font-weight: 600 !important;
            border-radius: 12px !important;
            transition: all 0.2s ease !important;

            .material-icons {
                font-size: 20px;
            }

            &:hover {
                background: #f8f9fa !important;
                transform: translateY(-1px);
            }
        }


        .modal-footer {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 12px;
            padding: 20px 28px;
            border-top: 2px solid #e8eaed;

            .supsis-button {
                height: 44px;
                padding: 0 24px;
                font-size: 15px;
                border-radius: 10px;
                
                .material-icons {
                    font-size: 18px;
                }
            }
        }
    }
}
</style>
